import { Flex, Grid } from '@radix-ui/themes';
import { PitchPerfPlotBreaks } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/break-stdev';
import { PitchPerfLegend } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/legend';
import { PitchPerfPlotPlate } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plate-location';
import { PlotHoverProvider } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-hover';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import { IPitchPerfPlot } from 'interfaces/i-pitch-performance';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MachinePerformanceHelper } from 'lib_ts/classes/machine-performance.helper';
import { IGroupedFires } from 'lib_ts/interfaces/data/i-pitch-performance';
import { useCallback, useMemo } from 'react';

const SHOW_LEGEND = false;

export const PitchPerfPlots = (props: IPitchPerfPlot) => {
  const selectedPitchId = usePitchPerformanceStore((s) => s.selectedPitchId);
  const selectedShotIds = usePitchPerformanceStore((s) => s.selectedShotIds);

  const mixinCallback = useCallback(
    (group: IGroupedFires) =>
      MachinePerformanceHelper.cloneWithAvgAndDelta({
        ...group,
        // only include fires related to the selected shots
        fires: group.fires.filter((f) => selectedShotIds.includes(f.shot._id)),
      }),
    [selectedShotIds]
  );

  /**
   * If the user has selected specific shots from the ShotTable,
   * we'll re-calculate our averages using those selected shots only.
   */
  const groups = useMemo(() => {
    if (selectedShotIds.length === 0) {
      return props.groups;
    }

    return props.groups
      .filter((g) =>
        // exclude visible groups if they don't include any of the selected shots
        ArrayHelper.hasIntersection(
          g.fires.map((f) => f.shot._id),
          selectedShotIds
        )
      )
      .map(mixinCallback);
  }, [props.groups, selectedShotIds, mixinCallback]);

  const refGroups = useMemo(() => {
    if (selectedShotIds.length === 0) {
      return props.refGroups;
    }

    return props.refGroups
      ?.filter((g) =>
        // exclude visible groups if they don't include any of the selected shots
        ArrayHelper.hasIntersection(
          g.fires.map((f) => f.shot._id),
          selectedShotIds
        )
      )
      .map(mixinCallback);
  }, [props.refGroups, selectedShotIds, mixinCallback]);

  return (
    <PlotHoverProvider>
      <Flex direction="column" gap="4">
        <Grid
          columns={{
            md: '1',
            lg: '2',
          }}
          gap="4"
        >
          <PitchPerfPlotPlate
            groups={groups}
            refGroups={refGroups}
            isHwDiagnostic={props.isHwDiagnostic}
            highlightCurrent={!!selectedPitchId}
          />

          <PitchPerfPlotBreaks
            groups={groups}
            refGroups={refGroups}
            isHwDiagnostic={props.isHwDiagnostic}
            highlightCurrent={!!selectedPitchId}
          />
        </Grid>

        {SHOW_LEGEND && <PitchPerfLegend />}
      </Flex>
    </PlotHoverProvider>
  );
};
